import { AxiosError } from 'axios'
import { STORAGE_KEYS, clearTokens } from '../constants/storageKeys'
import { ROUTES } from 'src/types/routes'
import { refreshTokenFn } from '../../api/routes/auth'

interface RetryConfig {
  headers: Record<string, string>
  _retry?: number
}

const MAX_RETRY_ATTEMPTS = 2

// A full reload drops router state, so the page to return to after sign-in rides in the URL.
const withReturnPath = (route: string) => `${route}?from=${encodeURIComponent(window.location.pathname)}`

export async function handleTokenRefresh(
  error: AxiosError,
  retryRequest: (config: RetryConfig) => Promise<AxiosError | unknown>,
) {
  const originalRequest = error.config as RetryConfig

  if (originalRequest._retry && originalRequest._retry >= MAX_RETRY_ATTEMPTS) {
    clearTokens()
    sessionStorage.clear() // clear COPA, tenant stuff on forced logout
    window.location.href = withReturnPath(ROUTES.SIGN_IN)
    return Promise.reject(new Error('Max retry attempts reached'))
  }

  try {
    const refreshToken =
      localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

    if (!refreshToken) {
      clearTokens()
      window.location.href = withReturnPath(ROUTES.SIGN_IN)
      return Promise.reject(new Error('No refresh token available'))
    }

    const response = await refreshTokenFn({ token: refreshToken })
    const storage = localStorage.getItem(STORAGE_KEYS.IS_REMEMBER_ME) === 'true' ? localStorage : sessionStorage
    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token)
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token)

    if (!originalRequest) {
      return Promise.reject(new Error('No original request config'))
    }

    return retryRequest({
      ...originalRequest,
      headers: {
        ...originalRequest.headers,
        Authorization: `Bearer ${response.access_token}`,
      },
      _retry: (originalRequest._retry || 0) + 1,
    })
  } catch (refreshError) {
    sessionStorage.clear() // clear COPA, tenant stuff on forced logout
    window.location.href = withReturnPath(ROUTES.LOGOUT)
    return Promise.reject(refreshError)
  }
}
